import React, { useState, useEffect } from "react";
import Dialog from "../../CoreComponent/Dialog";
import httpService from "../../common/httpService";
import toast from "react-hot-toast";
import ImageUpload from "../../CoreComponent/ImageUpload";
import { DataGrid } from "@mui/x-data-grid";
import {
  Typography,
  Tabs,
  Tab,
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  Paper,
} from "@mui/material";

const YesNoChip = ({ value }) => {
  const yes = value === 1 || value === true || value === "1";
  return (
    <Chip
      size="small"
      label={yes ? "Yes" : "No"}
      color={yes ? "success" : "default"}
      variant={yes ? "filled" : "outlined"}
    />
  );
};

const InfoItem = ({ label, value }) => (
  <Grid item xs={6} sm={4} md={3}>
    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.85rem' }}>
      {value ?? "-"}
    </Typography>
  </Grid>
);

const AttendanceComponent = () => {
  const [attendanceData, setAttendanceData] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [errorMsg, setErrorMsg] = useState("");

  const [isDialogOpen, setDialogOpen] = useState(false);
  const [attendanceIdForUpload, setAttendanceIdForUpload] = useState(0);
  const [file, setFile] = useState(null);

  // Details popup
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [selectedAttendance, setSelectedAttendance] = useState(null);

  // tab: "paid" | "unpaid"
  const [activeTab, setActiveTab] = useState("paid");

  const BaseUrl = process.env.REACT_APP_BASE_URL;
  const token = localStorage.getItem("token");

  const getAttendanceUrl = () => {
    if (activeTab === "paid") {
      return "https://eventscons.com/eventbackendnew/api/attendances/paid";
    }
    return "https://eventscons.com/eventbackendnew/api/attendances/unpaid";
  };

  const fetchAttendances = async (page = 1) => {
    try {
      await httpService({
        method: "GET",
        url: getAttendanceUrl(),
        params: { page },
        headers: { Authorization: `Bearer ${token}` },
        onSuccess: (data) => {
          const list = data.attendance || data.data || data.attendances || [];
          setAttendanceData(list);
          setTotalPages(data?.totalPages || 1);
          setCurrentPage(Number(data?.currentPage) || 1);
        },
        onError: (error) =>
          setErrorMsg(
            error?.message || "Failed to fetch attendance records."
          ),
        withToast: true,
      });
    } catch (error) {
      console.error(error);
      setErrorMsg("Failed to fetch attendance records.");
    }
  };

  const handleFileUpload = async () => {
    if (!file) {
      toast.error("Please select a file first");
      return;
    }

    const formData = new FormData();
    formData.append("certificatePDF", file);

    try {
      await httpService({
        method: "POST",
        url: `${BaseUrl}/attendances/${attendanceIdForUpload}/certificate`,
        headers: { Authorization: `Bearer ${token}` },
        data: formData,
        onSuccess: () => {
          toast.success("File uploaded successfully!");
          fetchAttendances(currentPage);
        },
        onError: (error) => console.error("Failed to upload file:", error),
      });
    } catch (error) {
      console.error("Failed to upload file:", error);
    }

    setDialogOpen(false);
    setFile(null);
  };

  const handlePageChange = (page) => {
    fetchAttendances(page);
  };

  const handleUploadClick = (attendance) => {
    setAttendanceIdForUpload(attendance.id);
    setDialogOpen(true);
  };

  const handleTabChange = (event, value) => {
    setActiveTab(value);
  };

  const handleViewDetails = (row) => {
    setSelectedAttendance(row?.original || null);
    setIsDetailsOpen(true);
  };

  // Table data
  const rows = attendanceData.map((attendance) => ({
    id: attendance.id,
    attendee_name: attendance?.user?.name || attendance?.user_name || "",
    email: attendance?.user?.email || attendance?.user_email || "",
    conference_title:
      attendance?.conference?.title || attendance?.conference_title || "",
    // payment_status column removed from table (still available in details via original)
    conference_bag: attendance.includes_conference_bag ? "Yes" : "No",
    conference_badge: attendance.includes_conference_badge ? "Yes" : "No",
    conference_book: attendance.includes_conference_book ? "Yes" : "No",
    certificate: attendance.includes_certificate ? "Yes" : "No",
    lecture_attendance: attendance.includes_lecture_attendance ? "Yes" : "No",
    original: attendance,
  }));

  const columns = [
    {
      field: "attendee_name",
      headerName: "Attendee Name",
      flex: 1,
      minWidth: 230,
      cellClassName: "centered-cell",
    },
    {
      field: "email",
      headerName: "Email",
      flex: 1,
      minWidth: 230,
      cellClassName: "centered-cell",
    },
    {
      field: "conference_title",
      headerName: "Conference Title",
      flex: 1,
      minWidth: 300,
      cellClassName: "centered-cell",
    },
    // Payment Status column REMOVED ✅
    {
      field: "conference_bag",
      headerName: "Conference Bag",
      flex: 1,
      minWidth: 180,
      cellClassName: "centered-cell",
    },
    {
      field: "conference_badge",
      headerName: "Conference Badge",
      flex: 1,
      minWidth: 190,
      cellClassName: "centered-cell",
    },
    {
      field: "conference_book",
      headerName: "Conference Book",
      flex: 1,
      minWidth: 180,
      cellClassName: "centered-cell",
    },
    {
      field: "certificate",
      headerName: "Certificate",
      flex: 1,
      minWidth: 160,
      cellClassName: "centered-cell",
    },
    {
      field: "lecture_attendance",
      headerName: "Lecture Attendance",
      flex: 1,
      minWidth: 210,
      cellClassName: "centered-cell",
    },
    // View details button ✅
    {
      field: "view_details",
      headerName: "View Details",
      minWidth: 170,
      flex: 1,
      sortable: false,
      filterable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Button
          variant="contained"
          size="small"
          onClick={() => handleViewDetails(params.row)}
        >
          View Details
        </Button>
      ),
    },
  ];

  // initial load
  useEffect(() => {
    fetchAttendances(1);
  }, []);

  // tab change par page reset + new API call
  useEffect(() => {
    setCurrentPage(1);
    fetchAttendances(1);
  }, [activeTab]);

  return (
    <div
      style={{
        borderRadius: "8px",
        width: "100%",
        maxWidth: "1700px",
        padding: "20px",
      }}
      className="custom-dialog"
    >
      <Typography
        variant="h6"
        sx={{
          color: "#c62828",
          fontWeight: "bold",
          fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" },
          textAlign: "center",
        }}
      >
        All Attendances
      </Typography>

      {/* Paid / Unpaid Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", mt: 2 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          aria-label="paid-unpaid-tabs"
        >
          <Tab label="Paid" value="paid" />
          <Tab label="Unpaid" value="unpaid" />
        </Tabs>
      </Box>

      <DataGrid
        getRowId={(row) => row.id}
        rows={rows}
        columns={columns}
        paginationModel={{ page: currentPage - 1, pageSize: 12 }}
        onPaginationModelChange={(pagination) => {
          const newPage = pagination.page + 1;
          setCurrentPage(newPage);
          handlePageChange(newPage);
        }}
        rowCount={totalPages * 12}
        pageSizeOptions={[12]}
        paginationMode="server"
        checkboxSelection
        disableRowSelectionOnClick
        autoHeight
        sx={{
          marginTop: "20px",
          "& .MuiDataGrid-virtualScroller": {
            overflow: "hidden",
          },
        }}
      />

      {/* Upload Certificate Dialog */}
      {isDialogOpen && (
        <Dialog
          viewHeader={true}
          header="Upload Certificate File"
          open={isDialogOpen}
          setOpen={setDialogOpen}
          className="dialog-file"
        >
          <div className="dialog-content">
            <ImageUpload
              required
              label="Certificate File"
              allowedExtensions={["pdf", "jpg", "jpeg", "png"]}
              inputValue={file}
              setInputValue={setFile}
              className="image-upload"
              placeholder="Choose a file"
            />
            <div className="dialog-actions">
              <button className="save-button" onClick={handleFileUpload}>
                Save
              </button>
              <button
                className="cancel-button"
                onClick={() => setDialogOpen(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </Dialog>
      )}

      {/* Details Dialog (Wide + Compact) - FIXED */}
      {isDetailsOpen && selectedAttendance && (
        <Dialog
          viewHeader={true}
          header="Attendance Details"
          open={isDetailsOpen}
          setOpen={setIsDetailsOpen}
          className="dialog-file wide-dialog"
          style={{ maxWidth: '95vw', width: '1200px' }}
        >
          <Box sx={{ p: 1 }}>
            <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
              {/* Header - Compact */}
              <Box sx={{ mb: 1.5 }}>
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center', 
                  flexWrap: 'wrap', 
                  gap: 1 
                }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontWeight: 900, 
                        lineHeight: 1.2, 
                        fontSize: '1.1rem' 
                      }}
                    >
                      {selectedAttendance?.user?.name || selectedAttendance?.user_name || "-"}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      color="text.secondary" 
                      sx={{ fontSize: '0.85rem' }}
                    >
                      {selectedAttendance?.user?.email || selectedAttendance?.user_email || "-"}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Chip
                      size="small"
                      label={(selectedAttendance?.payment_status || "-").toUpperCase()}
                      color={selectedAttendance?.payment_status === "paid" ? "success" : "warning"}
                      sx={{ fontSize: '0.7rem', height: 24 }}
                    />
                    <Chip
                      size="small"
                      label={selectedAttendance?.is_completed ? "Completed" : "Pending"}
                      color={selectedAttendance?.is_completed ? "success" : "default"}
                      variant={selectedAttendance?.is_completed ? "filled" : "outlined"}
                      sx={{ fontSize: '0.7rem', height: 24 }}
                    />
                  </Box>
                </Box>
              </Box>

              {/* Registration - 2 rows max */}
              <Typography 
                variant="subtitle2" 
                sx={{ fontWeight: 800, mb: 1, fontSize: '1rem' }}
              >
                Registration Details
              </Typography>
              <Grid container spacing={1}>
                <InfoItem label="ID" value={selectedAttendance?.id} />
                <InfoItem label="User ID" value={selectedAttendance?.user_id} />
                <InfoItem label="Conf ID" value={selectedAttendance?.conference_id} />
                <InfoItem label="Category" value={selectedAttendance?.category} />
                <InfoItem label="Fee" value={`$${selectedAttendance?.registration_fee || '-'}`} />
                <InfoItem label="Source" value={selectedAttendance?.hear_about_event} />
                <InfoItem label="PayPal ID" value={selectedAttendance?.paypal_order_id} />
                <InfoItem label="Barcode" value={selectedAttendance?.barcode} />
              </Grid>

              <Divider sx={{ my: 1.5 }} />

              {/* Conference - 1 row */}
              <Typography 
                variant="subtitle2" 
                sx={{ fontWeight: 800, mb: 1, fontSize: '1rem' }}
              >
                Conference
              </Typography>
              <Grid container spacing={1}>
                <InfoItem 
                  label="Title" 
                  value={selectedAttendance?.conference?.title || selectedAttendance?.conference_title} 
                />
                <InfoItem 
                  label="Location" 
                  value={selectedAttendance?.conference?.location} 
                />
                <InfoItem 
                  label="Status" 
                  value={selectedAttendance?.conference?.status} 
                />
              </Grid>

              <Divider sx={{ my: 1.5 }} />

              {/* Includes - Compact chips */}
              <Typography 
                variant="subtitle2" 
                sx={{ fontWeight: 800, mb: 1, fontSize: '1rem' }}
              >
                Includes
              </Typography>
              <Grid container spacing={1}>
                <Grid item xs={6} sm={4} md={3}>
                  <Typography 
                    variant="caption" 
                    color="text.secondary" 
                    sx={{ fontSize: '0.75rem', display: 'block' }}
                  >
                    Bag
                  </Typography>
                  <YesNoChip value={selectedAttendance?.includes_conference_bag} />
                </Grid>
                <Grid item xs={6} sm={4} md={3}>
                  <Typography 
                    variant="caption" 
                    color="text.secondary" 
                    sx={{ fontSize: '0.75rem', display: 'block' }}
                  >
                    Badge
                  </Typography>
                  <YesNoChip value={selectedAttendance?.includes_conference_badge} />
                </Grid>
                <Grid item xs={6} sm={4} md={3}>
                  <Typography 
                    variant="caption" 
                    color="text.secondary" 
                    sx={{ fontSize: '0.75rem', display: 'block' }}
                  >
                    Book
                  </Typography>
                  <YesNoChip value={selectedAttendance?.includes_conference_book} />
                </Grid>
                <Grid item xs={6} sm={4} md={3}>
                  <Typography 
                    variant="caption" 
                    color="text.secondary" 
                    sx={{ fontSize: '0.75rem', display: 'block' }}
                  >
                    Certificate
                  </Typography>
                  <YesNoChip value={selectedAttendance?.includes_certificate} />
                </Grid>
                <Grid item xs={6} sm={4} md={3}>
                  <Typography 
                    variant="caption" 
                    color="text.secondary" 
                    sx={{ fontSize: '0.75rem', display: 'block' }}
                  >
                    Lectures
                  </Typography>
                  <YesNoChip value={selectedAttendance?.includes_lecture_attendance} />
                </Grid>
              </Grid>

              {/* Close button */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Button 
                  variant="outlined" 
                  onClick={() => setIsDetailsOpen(false)}
                  size="small"
                >
                  Close
                </Button>
              </Box>
            </Paper>
          </Box>
        </Dialog>
      )}

      {errorMsg && (
        <Typography
          variant="body2"
          color="error"
          sx={{ marginTop: 2, textAlign: "center" }}
        >
          {errorMsg}
        </Typography>
      )}
    </div>
  );
};

export default AttendanceComponent;